import { useAssignedPhone } from 'hooks/api/assigned-phone';
import { formatPhoneNumber } from 'src/utils/formatting/phone';
import { useTwilioCallProvider } from './providers/twilio/call.provider';
import { useContactsWithCalls } from 'hooks/api/contacts/contacts';
import { usePhoneContext } from 'src/contexts/phone-context';
import { CallCallbacks, CallProvider, MatchedContact } from './types';
import { Route } from 'src/routes/__root';
import { useEffect } from 'react';
import { FilterParam, generateFilterQueryString } from 'src/utils/queryParams';
import { useBooleanSetting } from 'hooks/settings/settings';
import { SettingName } from 'src/api/generated';
import { useBridgeCallProvider } from './providers/bridge/call.provider';

export const useUserPhoneNumber = (): {
  phoneNumber: string;
  error: Error | null;
  loading: boolean;
} => {
  const { phone, error, loading } = useAssignedPhone();
  const phoneNumber = phone ? formatPhoneNumber(phone.number) : '';

  return { phoneNumber, error, loading };
};

/**
 * @deprecated Use `useGlobalCallProviderContext` instead.
 * The GlobalCallProvider handles provider selection and callback merging in a more robust way.
 *
 * @example
 * // Instead of:
 * const callProvider = useCallProvider(callbacks);
 * // Use:
 * import { useGlobalCallProviderContext } from 'src/contexts/global-call-provider-context';
 * const callProvider = useGlobalCallProviderContext();
 */
export const useCallProvider = (
  callCallbacks?: CallCallbacks
): CallProvider => {
  const { setting: callWithoutInternet } = useBooleanSetting(
    SettingName.CALL_WITHOUT_INTERNET
  );
  // Potentially use any provider
  const callProvider = useTwilioCallProvider(callCallbacks);
  const bridgeCallProvider = useBridgeCallProvider(callCallbacks);

  if (callWithoutInternet) return bridgeCallProvider;
  return callProvider;
};

export const useMatchContact = (
  validDisplayNumber: string | null
): MatchedContact => {
  const filters: Record<string, FilterParam> = {};
  if (validDisplayNumber) filters.phone = { value: validDisplayNumber };
  const filterBy = generateFilterQueryString(filters);

  const enabled = !!validDisplayNumber;
  const { contacts, loading } = useContactsWithCalls(
    {
      filterBy,
    },
    enabled
  );

  if (!contacts) return { contact: undefined, loading };

  const contact = contacts.find(
    (contact) => contact.phone === validDisplayNumber
  );

  return { contact, loading };
};

export const useSetPhoneNumberFromQueryParams = (): void => {
  const { setPhoneNumber } = usePhoneContext();
  const { phoneNumber } = Route.useSearch();

  useEffect(() => {
    if (phoneNumber) {
      setPhoneNumber(phoneNumber);
    }
  }, [phoneNumber, setPhoneNumber]);

  return;
};
